import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = React.memo(
  ({
    item,
    renderItem,
    depth = 0,
    isCollapsed,
    onToggleCollapse,
    collapseIcon,
    isParent,
    isItemCollapsed,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    const style = useMemo(() => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }), [transform, transition, isDragging]);

    const handleToggleCollapse = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleCollapse(item.id);
      },
      [item.id, onToggleCollapse]
    );

    // Only show collapse icon for parent items with children
    const shouldShowCollapseIcon =
      isParent && item.children && item.children.length > 0;

    // Memoize the rendered item to prevent unnecessary re-renders
    const renderedItem = useMemo(() => renderItem({
      collapseIcon: shouldShowCollapseIcon ? (
        <div
          onClick={handleToggleCollapse}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          {collapseIcon({ isCollapsed, item })}
        </div>
      ) : null,
      depth,
      handler: <div className="drag-handle">⋮⋮</div>,
      index: 0,
      item,
    }), [renderItem, shouldShowCollapseIcon, handleToggleCollapse, collapseIcon, isCollapsed, item, depth]);

    // Memoize children rendering
    const childrenContent = useMemo(() => {
      if (isCollapsed || !item.children || item.children.length === 0) {
        return null;
      }

      return (
        <div
          style={{
            backgroundColor: depth > 0 ? "#F9F8F7" : "transparent",
            marginTop: depth > 0 ? "10px" : "0",
          }}
        >
          <div style={{ marginLeft: depth * 20 }}>
            {item.children.map((child) => (
              <SortableItem
                key={child.id}
                item={child}
                renderItem={renderItem}
                depth={depth + 1}
                isCollapsed={isItemCollapsed(child.id)}
                onToggleCollapse={onToggleCollapse}
                collapseIcon={collapseIcon}
                isParent={child.children && child.children.length > 0}
                isItemCollapsed={isItemCollapsed}
              />
            ))}
          </div>
        </div>
      );
    }, [isCollapsed, item.children, depth, renderItem, isItemCollapsed, onToggleCollapse, collapseIcon]);

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {renderedItem}
        {childrenContent}
      </div>
    );
  }
);

SortableItem.displayName = "SortableItem";

const SortableHierarchy = React.memo(
  ({
    items,
    renderItem,
    onChange,
    renderCollapseIcon,
    className,
    maxDepth = 10,
  }) => {
    const [activeId, setActiveId] = useState(null);
    const [collapsedItems, setCollapsedItems] = useState(new Set());
    const [activeItem, setActiveItem] = useState(null);
    
    // Use refs to prevent unnecessary re-renders
    const itemsRef = useRef(items);
    const onChangeRef = useRef(onChange);
    
    // Update refs when props change
    React.useEffect(() => {
      itemsRef.current = items;
      onChangeRef.current = onChange;
    }, [items, onChange]);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8, // Only start dragging after 8px movement
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    // Memoize flattened items with better performance
    const flattenedItems = useMemo(() => {
      const flatten = (items, level = 0) => {
        const result = [];
        const stack = [{ items, level }];
        
        while (stack.length > 0) {
          const { items: currentItems, level: currentLevel } = stack.pop();
          
          for (let i = currentItems.length - 1; i >= 0; i--) {
            const item = currentItems[i];
            result.unshift({ ...item, level: currentLevel });
            
            if (item.children && item.children.length > 0 && !collapsedItems.has(item.id)) {
              stack.push({ items: item.children, level: currentLevel + 1 });
            }
          }
        }
        
        return result;
      };
      return flatten(items);
    }, [items, collapsedItems]);

    // Memoize item IDs for SortableContext
    const itemIds = useMemo(
      () => flattenedItems.map((item) => item.id),
      [flattenedItems]
    );

    // Optimized toggle collapse function
    const toggleCollapse = useCallback((itemId) => {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
    }, []);

    // Check if an item is collapsed
    const isItemCollapsed = useCallback(
      (itemId) => collapsedItems.has(itemId),
      [collapsedItems]
    );

    // Optimized drag start handler
    const handleDragStart = useCallback((event) => {
      const { active } = event;
      setActiveId(active.id);
      const item = flattenedItems.find((item) => item.id === active.id);
      setActiveItem(item);
    }, [flattenedItems]);

    // Optimized drag end handler with debouncing
    const handleDragEnd = useCallback((event) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveItem(null);

      if (active.id !== over?.id && over) {
        const oldIndex = flattenedItems.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = flattenedItems.findIndex(
          (item) => item.id === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          // Use requestAnimationFrame to prevent blocking the UI
          requestAnimationFrame(() => {
            try {
              // Create new array with reordered items
              const newFlattenedItems = [...flattenedItems];
              const [movedItem] = newFlattenedItems.splice(oldIndex, 1);
              newFlattenedItems.splice(newIndex, 0, movedItem);

              // Optimized hierarchy reconstruction
              const reconstructHierarchy = (flatItems) => {
                const result = [];
                const itemMap = new Map();
                const levelMap = new Map();

                // Create map of all items and level map
                flatItems.forEach((item) => {
                  itemMap.set(item.id, { ...item, children: [] });
                  if (!levelMap.has(item.level)) {
                    levelMap.set(item.level, []);
                  }
                  levelMap.get(item.level).push(item);
                });

                // Build hierarchy level by level
                for (let level = 0; level <= Math.max(...levelMap.keys()); level++) {
                  const levelItems = levelMap.get(level) || [];
                  
                  levelItems.forEach((item) => {
                    const currentItem = itemMap.get(item.id);
                    if (level === 0) {
                      result.push(currentItem);
                    } else {
                      // Find parent efficiently
                      const parentLevel = level - 1;
                      const parentItems = levelMap.get(parentLevel) || [];
                      
                      for (let i = parentItems.length - 1; i >= 0; i--) {
                        const parent = itemMap.get(parentItems[i].id);
                        if (parent && parent.children) {
                          parent.children.push(currentItem);
                          break;
                        }
                      }
                    }
                  });
                }

                return result;
              };

              const newItems = reconstructHierarchy(newFlattenedItems);

              // Call onChange callback
              if (onChangeRef.current) {
                onChangeRef.current({
                  dragItem: { id: active.id },
                  targetPath: [over.id],
                  items: newItems,
                });
              }
            } catch (error) {
              console.error('Error during drag and drop:', error);
            }
          });
        }
      }
    }, [flattenedItems]);

    // Memoize the main content to prevent unnecessary re-renders
    const mainContent = useMemo(() => (
      <SortableContext
        items={itemIds}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item) => (
          <SortableItem
            key={item.id}
            item={item}
            renderItem={renderItem}
            depth={item.children && item.children.length > 0 ? 1 : 0}
            isCollapsed={isItemCollapsed(item.id)}
            onToggleCollapse={toggleCollapse}
            collapseIcon={renderCollapseIcon}
            isParent={item.children && item.children.length > 0}
            isItemCollapsed={isItemCollapsed}
          />
        ))}
      </SortableContext>
    ), [itemIds, items, renderItem, isItemCollapsed, toggleCollapse, renderCollapseIcon]);

    // Memoize drag overlay
    const dragOverlay = useMemo(() => (
      <DragOverlay>
        {activeId && activeItem ? (
          <div style={{ opacity: 0.8 }}>
            {renderItem({
              collapseIcon: null,
              depth: 0,
              handler: null,
              index: 0,
              item: activeItem,
            })}
          </div>
        ) : null}
      </DragOverlay>
    ), [activeId, activeItem, renderItem]);

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={className}>
          {mainContent}
        </div>
        {dragOverlay}
      </DndContext>
    );
  }
);

SortableHierarchy.displayName = "SortableHierarchy";

export default SortableHierarchy;
